import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { FiTruck, FiCheckCircle, FiMapPin, FiFileText, FiPaperclip } from 'react-icons/fi';
import Button from '../../components/button';
import FileUpload from '../../components/FileUpload';
import Input from '../../components/input';
import Select from '../../components/Select';
import { apiService } from '../../services/apiService';
import { AddFleetStatusEntryValidationSchema } from '../../utils/Schema';
import { errorNotify } from '../../utils/toast';
import '../../styles/AddFleetStatusEntry.css';

const STATUS_OPTIONS = [
  { value: 'RUNNING', label: 'Running' },
  { value: 'IDLE', label: 'Idle' },
  { value: 'STOP', label: 'Stop' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const initialValues = {
  vehicle_id: '',
  status: '',
  status_date: new Date().toISOString().slice(0, 10),
  status_time: '',
  location: '',
  customer_id: '',
  origin: '',
  destination: '',
  remarks: '',
};

const AddFleetStatusEntry = ({ onClose }) => {
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [vehicleOptionsLoading, setVehicleOptionsLoading] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerOptionsLoading, setCustomerOptionsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const fetchVehicles = async () => {
      setVehicleOptionsLoading(true);
      try {
        const response = await apiService.getFmcrVehicleDropdown('all');
        const rows = response?.data || response || [];
        if (isMounted) {
          setVehicleOptions((Array.isArray(rows) ? rows : []).map((v) => ({
            value: v.id ?? v.value,
            label: v.registration_number || v.label || String(v.id ?? v.value),
          })));
        }
      } catch (err) {
        console.error('Failed to fetch vehicle dropdown:', err);
      } finally {
        if (isMounted) setVehicleOptionsLoading(false);
      }
    };

    const fetchCustomers = async () => {
      setCustomerOptionsLoading(true);
      try {
        const response = await apiService.getCustomerDropdown();
        const rows = response?.data?.results || response?.data || response?.results || response || [];
        if (isMounted) {
          setCustomerOptions((Array.isArray(rows) ? rows : []).map((c) => ({
            value: c.id ?? c.value,
            label: c.name || c.label || String(c.id ?? c.value),
          })));
        }
      } catch (err) {
        console.error('Failed to fetch customer dropdown:', err);
      } finally {
        if (isMounted) setCustomerOptionsLoading(false);
      }
    };

    fetchVehicles();
    fetchCustomers();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // No backend endpoint exists yet for saving a fleet status entry - surface that clearly
      // instead of pretending to persist the entry.
      errorNotify("Saving isn't connected to the backend yet, so this entry wasn't saved.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={AddFleetStatusEntryValidationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, submitForm }) => (
        <div>
          <p className="afs_modal_subtitle">Create a new fleet status entry for the selected vehicle.</p>

          <div className="afs_modal_body">
            <div className="afs_col">
              <div className="afs_section">
                <div className="afs_section_header afs_purple">
                  <FiTruck />
                  <span>Vehicle Information</span>
                </div>
                <div className="afs_section_body">
                  <Select
                    label="Registration No."
                    placeholder="Select Vehicle"
                    options={vehicleOptions}
                    loading={vehicleOptionsLoading}
                    name="vehicle_id"
                    value={vehicleOptions.find((option) => option.value === values.vehicle_id) || null}
                    onChange={(selectedOption) => setFieldValue('vehicle_id', selectedOption?.value || '')}
                    onBlur={handleBlur}
                    error={errors.vehicle_id}
                    touched={touched.vehicle_id}
                    isSearch={true}
                  />
                </div>
              </div>

              <div className="afs_section">
                <div className="afs_section_header afs_green">
                  <FiCheckCircle />
                  <span>Status Details</span>
                </div>
                <div className="afs_section_body">
                  <Select
                    label="Current Status"
                    placeholder="Select Status"
                    options={STATUS_OPTIONS}
                    loading={false}
                    name="status"
                    value={STATUS_OPTIONS.find((option) => option.value === values.status) || null}
                    onChange={(selectedOption) => setFieldValue('status', selectedOption?.value || '')}
                    onBlur={handleBlur}
                    error={errors.status}
                    touched={touched.status}
                    isSearch={false}
                  />

                  <div className="afs_date_time_row">
                    <div>
                      <Input
                        label="Status Date"
                        type="date"
                        name="status_date"
                        value={values.status_date}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.status_date}
                        touched={touched.status_date}
                      />
                    </div>
                    <div>
                      <Input
                        label="Status Time"
                        type="time"
                        name="status_time"
                        value={values.status_time}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.status_time}
                        touched={touched.status_time}
                      />
                    </div>
                  </div>

                  <Input
                    label="Current Location"
                    type="text"
                    placeholder="e.g. Landhi ISL, M-2 Islamabad"
                    name="location"
                    value={values.location}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.location}
                    touched={touched.location}
                  />

                  <Select
                    label="Customer"
                    placeholder="Select Customer"
                    options={customerOptions}
                    loading={customerOptionsLoading}
                    name="customer_id"
                    value={customerOptions.find((option) => option.value === values.customer_id) || null}
                    onChange={(selectedOption) => setFieldValue('customer_id', selectedOption?.value || '')}
                    onBlur={handleBlur}
                    error={errors.customer_id}
                    touched={touched.customer_id}
                    isSearch={true}
                    required={false}
                  />

                  <Input
                    label="Cost Center"
                    type="text"
                    name="cost_center"
                    value="-"
                    disabled={true}
                    readOnly={true}
                    required={false}
                  />
                </div>
              </div>
            </div>

            <div className="afs_col">
              <div className="afs_section">
                <div className="afs_section_header afs_purple">
                  <FiMapPin />
                  <span>Trip Information (Optional)</span>
                </div>
                <div className="afs_section_body">
                  <Input
                    label="Origin"
                    type="text"
                    placeholder="e.g. Karachi, EPRF 2"
                    name="origin"
                    value={values.origin}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.origin}
                    touched={touched.origin}
                    required={false}
                  />
                  <Input
                    label="Destination"
                    type="text"
                    placeholder="e.g. Port Qasim, Lahore"
                    name="destination"
                    value={values.destination}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.destination}
                    touched={touched.destination}
                    required={false}
                  />
                </div>
              </div>

              <div className="afs_section">
                <div className="afs_section_header afs_purple">
                  <FiFileText />
                  <span>Additional Information</span>
                </div>
                <div className="afs_section_body">
                  <Input
                    label="Remarks"
                    type="text"
                    placeholder="Add any operational notes or status remarks..."
                    name="remarks"
                    value={values.remarks}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.remarks}
                    touched={touched.remarks}
                    required={false}
                  />
                </div>
              </div>

              <div className="afs_section">
                <div className="afs_section_header afs_purple">
                  <FiPaperclip />
                  <span>Attachments (Optional)</span>
                </div>
                <div className="afs_section_body">
                  <FileUpload
                    destination="fmcr-vehicle-status"
                    files={uploadedFiles}
                    setFiles={setUploadedFiles}
                    maxFileSize={5}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="afs_footer">
            <Button
              cancelBtn={onClose}
              submitBtn={(e) => {
                e.preventDefault();
                e.stopPropagation();
                submitForm();
              }}
              loading={isSubmitting}
              disabled={isSubmitting}
              submitText="Save Entry"
              type="button"
              shouldRow={false}
            />
          </div>
        </div>
      )}
    </Formik>
  );
};

export default AddFleetStatusEntry;
